import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';
// Endpoints relativos para usar baseURL y fallback del cliente
const ENDPOINTS = {
  current: '/cash-register/current',
  all: '/cash-register/all',
  open: '/cash-register/open',
  close: '/cash-register/close',
  expense: '/cash-register/expense',
};

const CashRegister = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const canSeeTotals = user?.role === 'owner' || user?.role === 'admin';

  const [currentCash, setCurrentCash] = useState(null);
  const [history, setHistory] = useState([]);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const [openData, setOpenData] = useState({
    initialAmount: '',
    notes: '',
  });

  const [closeData, setCloseData] = useState({
    actualAmount: '',
    notes: '',
  });

  const [expenseData, setExpenseData] = useState({
    description: '',
    amount: '',
  });

  const fetchCurrentCash = useCallback(async () => {
    try {
      const res = await httpRequest.get(ENDPOINTS.current);
      setCurrentCash(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await httpRequest.get(ENDPOINTS.all);
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchCurrentCash();
    fetchHistory();
  }, [fetchCurrentCash, fetchHistory]);

  const handleOpenCash = async (e) => {
    e.preventDefault();
    if (!openData.initialAmount) {
      toast.warning('⚠️ Ingresa el monto inicial');
      return;
    }

    try {
      await httpRequest.post(ENDPOINTS.open, openData);
      toast.success('✅ Caja abierta correctamente');
      setShowOpenModal(false);
      setOpenData({ initialAmount: '', notes: '' });
      fetchCurrentCash();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || '❌ Error al abrir caja');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseData.description || !expenseData.amount) {
      toast.warning('⚠️ Descripción y monto son obligatorios');
      return;
    }

    try {
      await httpRequest.post(ENDPOINTS.expense, expenseData);
      toast.success('✅ Gasto registrado');
      setShowExpenseModal(false);
      setExpenseData({ description: '', amount: '' });
      fetchCurrentCash();
    } catch (err) {
      toast.error(err.response?.data?.message || '❌ Error al registrar gasto');
    }
  };

  const handleCloseCash = async (e) => {
    e.preventDefault();
    if (!closeData.actualAmount) {
      toast.warning('⚠️ Ingresa el monto real en caja');
      return;
    }

    if (!window.confirm('⚠️ ¿Estás seguro de cerrar la caja?')) return;

    try {
      await httpRequest.post(ENDPOINTS.close, closeData);
      toast.success('✅ Caja cerrada correctamente');
      setShowCloseModal(false);
      setIsVerified(false);
      setCloseData({ actualAmount: '', notes: '' });
      setCurrentCash(null);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || '❌ Error al cerrar caja');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const printReceipt = (cash) => {
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) return toast.error('Permite los popups para imprimir');

    // Calcular totales para el ticket
    const diff = (cash.actualAmount || 0) - (cash.expectedAmount || 0);

    const html = `
      <html>
      <head>
        <title>Cierre de Caja</title>
        <style>
          @page { margin: 0; size: 80mm auto; }
          body { 
            font-family: 'Courier New', monospace; 
            margin: 0; 
            padding: 10px; 
            width: 80mm; 
            font-size: 12px;
            color: black;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { fontWeight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          h2 { margin: 5px 0; font-size: 16px; }
          p { margin: 2px 0; }
        </style>
      </head>
      <body>
        <div class="center">
          <h2>CIERRE DE CAJA</h2>
          <p>${formatDate(cash.closingDate || new Date())}</p>
          <p>Cagiero: ${cash.user?.name || 'Desconocido'}</p>
        </div>
        <div class="line"></div>
        
        <p><strong>Apertura:</strong> ${formatDate(cash.openingDate)}</p>
        <div class="row"><p>Monto Inicial:</p> <p>${formatCurrency(cash.initialAmount)}</p></div>
        
        <div class="line"></div>
        <p class="center"><strong>VENTAS</strong></p>
        <div class="row"><p>Efectivo:</p> <p>${formatCurrency(cash.cashSales)}</p></div>
        <div class="row"><p>Tarjeta:</p> <p>${formatCurrency(cash.cardSales)}</p></div>
        <div class="row"><p>Transferencia:</p> <p>${formatCurrency(cash.transferSales)}</p></div>
        <div class="row bold"><p>TOTAL VENTAS:</p> <p>${formatCurrency(cash.totalSales)}</p></div>

        <div class="line"></div>
        <div class="row"><p>Total Gastos:</p> <p>-${formatCurrency(cash.totalExpenses)}</p></div>

        <div class="line"></div>
        <div class="row bold" style="font-size: 14px"><p>ESPERADO:</p> <p>${formatCurrency(cash.expectedAmount)}</p></div>
        <div class="row bold" style="font-size: 14px"><p>REAL (ARQUEO):</p> <p>${formatCurrency(cash.actualAmount)}</p></div>
        
         <div class="row bold"><p>DIFERENCIA:</p> <p>${formatCurrency(diff)}</p></div>

        <div class="line"></div>
        <br/><br/>
        <div class="center">
          __________________________<br/>
          Firma Responsable
        </div>
        <br/>
        <div class="center">
          <p>-- Fin del Reporte --</p>
        </div>
        <script>
          window.print();
          setTimeout(() => window.close(), 500);
        </script>
      </body>
      </html>
    `;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1>💰 Gestión de Caja</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={btnNav} onClick={() => navigate('/')}>
            🏠 Dashboard
          </button>
          <button style={{ ...btnNav, background: '#28a745' }} onClick={() => navigate('/sales')}>
            💵 Punto de Venta
          </button>
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>
            🚪 Salir
          </button>
        </div>
      </div>

      {/* Caja Actual */}
      {!currentCash ? (
        <div style={noCashStyle}>
          <h2>📭 No hay caja abierta</h2>
          <p>Abre una caja para comenzar a operar</p>
          <button style={btnPrimary} onClick={() => setShowOpenModal(true)}>
            🔓 Abrir Caja
          </button>
        </div>
      ) : (
        <div style={cashCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>🔓 Caja Abierta</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={btnWarning} onClick={() => setShowExpenseModal(true)}>
                💸 Registrar Gasto
              </button>
              <button style={btnDanger} onClick={() => setShowCloseModal(true)}>
                🔒 Cerrar Caja
              </button>
            </div>
          </div>

          <div style={infoGridStyle}>
            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Apertura</div>
              <div style={infoValueStyle}>{formatDate(currentCash.openingDate)}</div>
            </div>

            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Monto Inicial</div>
              <div style={{ ...infoValueStyle, color: '#007bff' }}>
                {formatCurrency(currentCash.initialAmount)}
              </div>
            </div>

            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Ventas Efectivo</div>
              <div style={{ ...infoValueStyle, color: '#28a745' }}>
                {formatCurrency(currentCash.cashSales)}
              </div>
            </div>

            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Ventas Tarjeta</div>
              <div style={{ ...infoValueStyle, color: '#17a2b8' }}>
                {formatCurrency(currentCash.cardSales)}
              </div>
            </div>

            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Ventas Transferencia</div>
              <div style={{ ...infoValueStyle, color: '#6f42c1' }}>
                {formatCurrency(currentCash.transferSales)}
              </div>
            </div>

            {canSeeTotals && (
              <div style={infoCardStyle}>
                <div style={infoLabelStyle}>Total Ventas</div>
                <div style={{ ...infoValueStyle, color: '#28a745', fontSize: '24px' }}>
                  {formatCurrency(currentCash.totalSales)}
                </div>
              </div>
            )}

            <div style={infoCardStyle}>
              <div style={infoLabelStyle}>Gastos</div>
              <div style={{ ...infoValueStyle, color: '#dc3545' }}>
                {formatCurrency(currentCash.totalExpenses)}
              </div>
            </div>

            {canSeeTotals ? (
              <div style={infoCardStyle}>
                <div style={infoLabelStyle}>Efectivo Esperado</div>
                <div style={{ ...infoValueStyle, color: '#fd7e14', fontSize: '24px' }}>
                  {formatCurrency(currentCash.expectedAmount)}
                </div>
              </div>
            ) : (
              <div style={infoCardStyle}>
                <div style={infoLabelStyle}>Efectivo Esperado</div>
                <div style={{ ...infoValueStyle, color: '#999', fontSize: '18px' }}>
                  🔐 Oculto
                </div>
              </div>
            )}
          </div>

          {/* Gastos */}
          {currentCash.expenses && currentCash.expenses.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3>📋 Gastos del Día</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead style={{ backgroundColor: '#dc3545', color: 'white' }}>
                    <tr>
                      <th style={thStyle}>Fecha</th>
                      <th style={thStyle}>Descripción</th>
                      <th style={thStyle}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCash.expenses.map((expense) => (
                      <tr key={expense._id}>
                        <td style={tdStyle}>{formatDate(expense.date)}</td>
                        <td style={tdStyle}>{expense.description}</td>
                        <td style={{ ...tdStyle, color: '#dc3545', fontWeight: 'bold' }}>
                          {formatCurrency(expense.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <div style={{ marginTop: '30px' }}>
        <h2>📊 Historial de Cajas</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: '#007bff', color: 'white' }}>
              <tr>
                <th style={thStyle}>Usuario</th>
                <th style={thStyle}>Apertura</th>
                <th style={thStyle}>Cierre</th>
                <th style={thStyle}>Inicial</th>
                <th style={thStyle}>Efectivo</th>
                <th style={thStyle}>Tarjeta</th>
                <th style={thStyle}>Transfer.</th>
                <th style={thStyle}>Gastos</th>
                {canSeeTotals && <th style={thStyle}>Esperado</th>}
                <th style={thStyle}>Real</th>
                {canSeeTotals && <th style={thStyle}>Diferencia</th>}
                <th style={thStyle}>Estado</th>
                {canSeeTotals && <th style={thStyle}>Imprimir</th>}
              </tr>
            </thead>
            <tbody>
              {history.map((cash) => (
                <tr key={cash._id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 'bold' }}>{cash.user?.name || 'Desconocido'}</div>
                  </td>
                  <td style={tdStyle}>{formatDate(cash.openingDate)}</td>
                  <td style={tdStyle}>
                    {cash.closingDate ? formatDate(cash.closingDate) : '-'}
                  </td>
                  <td style={tdStyle}>{formatCurrency(cash.initialAmount)}</td>
                  <td style={{ ...tdStyle, color: '#28a745', fontWeight: 'bold' }}>
                    {formatCurrency(cash.cashSales)}
                  </td>
                  <td style={{ ...tdStyle, color: '#17a2b8' }}>
                    {formatCurrency(cash.cardSales)}
                  </td>
                  <td style={{ ...tdStyle, color: '#6f42c1' }}>
                    {formatCurrency(cash.transferSales)}
                  </td>
                  <td style={{ ...tdStyle, color: '#dc3545' }}>
                    {formatCurrency(cash.totalExpenses)}
                  </td>
                  {canSeeTotals && (
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                      {formatCurrency(cash.expectedAmount)}
                    </td>
                  )}
                  <td style={tdStyle}>
                    {cash.actualAmount ? formatCurrency(cash.actualAmount) : '-'}
                  </td>
                  {canSeeTotals && (
                    <td
                      style={{
                        ...tdStyle,
                        color: cash.difference > 0 ? '#28a745' : cash.difference < 0 ? '#dc3545' : '#000',
                        fontWeight: 'bold',
                      }}
                    >
                      {cash.difference !== 0 && cash.status === 'closed'
                        ? formatCurrency(cash.difference)
                        : '-'}
                    </td>
                  )}
                  <td style={tdStyle}>
                    {cash.status === 'open' ? (
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>🔓 Abierta</span>
                    ) : (
                      <span style={{ color: '#6c757d' }}>🔒 Cerrada</span>
                    )}
                  </td>
                  {canSeeTotals && (
                    <td style={tdStyle}>
                      {cash.status === 'closed' && (
                        <button
                          onClick={() => printReceipt(cash)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 20 }}
                          title="Imprimir Ticket de Cierre"
                        >
                          🖨️
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Abrir Caja */}
      {showOpenModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2>🔓 Abrir Caja</h2>
            <form onSubmit={handleOpenCash}>
              <div style={formGroupStyle}>
                <label>Monto Inicial *</label>
                <input
                  type="number"
                  step="0.01"
                  value={openData.initialAmount}
                  onChange={(e) => setOpenData({ ...openData, initialAmount: e.target.value })}
                  style={inputStyle}
                  placeholder="0.00"
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label>Notas</label>
                <textarea
                  value={openData.notes}
                  onChange={(e) => setOpenData({ ...openData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px' }}
                  placeholder="Observaciones..."
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" style={btnSuccess}>
                  ✅ Abrir Caja
                </button>
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => setShowOpenModal(false)}
                >
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Registrar Gasto */}
      {showExpenseModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2>💸 Registrar Gasto</h2>
            <form onSubmit={handleAddExpense}>
              <div style={formGroupStyle}>
                <label>Descripción *</label>
                <input
                  type="text"
                  value={expenseData.description}
                  onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                  style={inputStyle}
                  placeholder="Ej: Compra de insumos"
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label>Monto *</label>
                <input
                  type="number"
                  step="0.01"
                  value={expenseData.amount}
                  onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                  style={inputStyle}
                  placeholder="0.00"
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" style={btnWarning}>
                  ✅ Registrar
                </button>
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => setShowExpenseModal(false)}
                >
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Cerrar Caja */}
      {showCloseModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2>🔒 Cerrar Caja (Arqueo)</h2>

            {!isVerified ? (
              // FASE 1: CONTEO CIEGO
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <p style={{ marginBottom: '15px', color: '#666' }}>
                  Por favor, cuenta el dinero físico en la caja e ingrésalo abajo.
                  <br />
                  <strong>No te diremos cuánto debería haber hasta que lo ingreses.</strong>
                </p>
              </div>
            ) : (
              // FASE 2: RESULTADO DEL ARQUEO
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <p style={{ margin: '5px 0' }}>
                  <strong>Efectivo Esperado:</strong>{' '}
                  <span style={{ color: '#007bff', fontSize: '18px', fontWeight: 'bold' }}>
                    {formatCurrency(currentCash?.expectedAmount)}
                  </span>
                </p>
                <div style={{ margin: '15px 0', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                  <strong>Tu Conteo: </strong>
                  <span>{formatCurrency(closeData.actualAmount)}</span>
                </div>
                <div>
                  <strong>Diferencia: </strong>
                  <span
                    style={{
                      color:
                        closeData.actualAmount - currentCash?.expectedAmount >= 0
                          ? '#28a745'
                          : '#dc3545',
                      fontSize: '22px',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatCurrency(closeData.actualAmount - currentCash?.expectedAmount)}
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                  * Si la diferencia es negativa, falta dinero.
                </p>
              </div>
            )}

            <form onSubmit={handleCloseCash}>
              <div style={formGroupStyle}>
                <label>Efectivo Real en Caja *</label>
                <input
                  type="number"
                  step="0.01"
                  value={closeData.actualAmount}
                  onChange={(e) => {
                    setIsVerified(false); // Resetear si cambia el valor
                    setCloseData({ ...closeData, actualAmount: e.target.value })
                  }}
                  style={{ ...inputStyle, fontSize: '18px', padding: '15px', textAlign: 'center' }}
                  placeholder="$ 0.00"
                  required
                  autoFocus
                />
              </div>

              <div style={formGroupStyle}>
                <label>Notas / Observaciones</label>
                <textarea
                  value={closeData.notes}
                  onChange={(e) => setCloseData({ ...closeData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '60px' }}
                  placeholder="Justificación de diferencias..."
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                {!isVerified ? (
                  <button
                    type="button"
                    style={{ ...btnWarning, width: '100%' }}
                    onClick={() => {
                      if (!closeData.actualAmount) return toast.warning('Ingresa el monto primero');
                      setIsVerified(true);
                    }}
                  >
                    🔍 Verificar Arqueo
                  </button>
                ) : (
                  <button type="submit" style={{ ...btnDanger, width: '100%' }}>
                    🔒 Confirmar Cierre
                  </button>
                )}

                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => {
                    setShowCloseModal(false);
                    setIsVerified(false);
                    setCloseData({ actualAmount: '', notes: '' });
                  }}
                >
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Estilos
const containerStyle = {
  padding: '30px',
  fontFamily: 'Arial, sans-serif',
  maxWidth: '1400px',
  margin: '0 auto',
};

const headerStyle = {
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const btnNav = {
  padding: '10px 20px',
  borderRadius: '5px',
  border: 'none',
  background: '#007bff',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
};

const noCashStyle = {
  textAlign: 'center',
  padding: '60px',
  background: 'white',
  borderRadius: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const btnPrimary = {
  padding: '15px 30px',
  fontSize: '18px',
  borderRadius: '8px',
  border: 'none',
  background: '#007bff',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 'bold',
  marginTop: '20px',
};

const cashCardStyle = {
  background: 'white',
  padding: '30px',
  borderRadius: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  marginBottom: '20px',
};

const btnWarning = {
  padding: '10px 20px',
  borderRadius: '5px',
  border: 'none',
  background: '#ffc107',
  color: '#000',
  cursor: 'pointer',
  fontWeight: 'bold',
};

const btnDanger = {
  padding: '10px 20px',
  borderRadius: '5px',
  border: 'none',
  background: '#dc3545',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 'bold',
};

const infoGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '15px',
  marginTop: '20px',
};

const infoCardStyle = {
  background: '#f8f9fa',
  padding: '15px',
  borderRadius: '8px',
  textAlign: 'center',
};

const infoLabelStyle = {
  fontSize: '14px',
  color: '#6c757d',
  marginBottom: '8px',
};

const infoValueStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#000',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  borderRadius: '8px',
  overflow: 'hidden',
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  borderBottom: '1px solid #ddd',
};

const tdStyle = {
  padding: '12px',
  borderBottom: '1px solid #f0f0f0',
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'white',
  padding: '30px',
  borderRadius: '10px',
  minWidth: '400px',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflowY: 'auto',
};

const formGroupStyle = {
  marginBottom: '15px',
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '5px',
  border: '1px solid #ddd',
  fontSize: '14px',
  marginTop: '5px',
};

const btnSuccess = {
  padding: '10px 20px',
  borderRadius: '5px',
  border: 'none',
  background: '#28a745',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 'bold',
  flex: 1,
};

const btnSecondary = {
  padding: '10px 20px',
  borderRadius: '5px',
  border: 'none',
  background: '#6c757d',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 'bold',
  flex: 1,
};

export default CashRegister;
